import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { hasRole } from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import ScreenError from '../../../ui/messageBanners/ScreenError';

import ModuleTemplate from './ModuleTemplate';
import ModuleTemplateEditor from './ModuleTemplateEditor';

import app from '../../../../app.module.css';
import cards from '../../../../commonStyles/cardContainer.module.css';
import local from '../../courseStyles/moduleTemplateManagementStyle.module.css';

const ModuleTemplateManagement = ({
    moduleTemplates,
    barrierNameDetails,
    roles,
    acceptedRoles,
    sessionBased = false,
    onUpdate,
    modulesError = {},
    onClearError
}) => {
    // LOCAL STATE
    const [showCreateModuleModal, setShowCreateModuleModal] = useState(false);
    const [editModuleType, setEditModuleType] = useState('create');
    const [currentModule, setCurrentModule] = useState({});

    // EVENT HANDLERS
    const onAddModule = () => {
        modulesError.error && onClearError('modules');
        setShowCreateModuleModal(true);
        setEditModuleType('create');
        setCurrentModule({});
    };

    const onEditModule = (entry) => {
        modulesError.error && onClearError('modules');
        setShowCreateModuleModal(true);
        setEditModuleType('edit');
        setCurrentModule(entry);
    };

    const addModule = (entry) => {
        onUpdate('moduleTemplates', [...moduleTemplates, entry]);
        setShowCreateModuleModal(false);
        setCurrentModule({});
    };

    const updateModule = (entry) => {
        onUpdate(
            'moduleTemplates',
            moduleTemplates.map((el) => (el.id === entry.id ? entry : el))
        );
        setShowCreateModuleModal(false);
        setCurrentModule({});
    };

    const onCancelModule = () => {
        setShowCreateModuleModal(false);
        setCurrentModule({});
    };

    // RENDER
    return (
        <div className={local.moduleManagementWrapper}>
            <h2 className={app.sectionHeading}>Module Templates:</h2>
            <div className={`${cards.cards} ${cards.cardContainer}`}>
                {moduleTemplates.length > 0 &&
                    moduleTemplates.map((el) => (
                        <div key={el.id}>
                            <ModuleTemplate
                                entry={el}
                                barrierNameDetails={barrierNameDetails || []}
                                disabled={!hasRole(acceptedRoles, roles)}
                                onEdit={onEditModule}
                                roles={roles}
                                acceptedRoles={acceptedRoles}
                            />
                        </div>
                    ))}
            </div>
            {showCreateModuleModal && (
                <ModuleTemplateEditor
                    type={editModuleType}
                    entry={currentModule}
                    barrierNameDetails={barrierNameDetails || []}
                    disabled={!hasRole(acceptedRoles, roles)}
                    sessionBased={sessionBased}
                    roles={roles}
                    onAdd={addModule}
                    onUpdate={updateModule}
                    isOpen={showCreateModuleModal}
                    onClose={onCancelModule}
                    existingModules={moduleTemplates}
                />
            )}

            <div className={local.moduleActions}>
                <Button
                    id="addModuleTemplate"
                    type="button"
                    content="Add a Module"
                    onClick={onAddModule}
                />

                {modulesError?.error && (
                    <ScreenError
                        error={modulesError}
                        name="modulesError"
                        clearError={onClearError}
                        allowDismiss={false}
                    />
                )}
            </div>
        </div>
    );
};

ModuleTemplateManagement.propTypes = {
    moduleTemplates: PropTypes.arrayOf(PropTypes.object),
    roles: PropTypes.arrayOf(PropTypes.string),
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    barrierNameDetails: PropTypes.array.isRequired,
    onUpdate: PropTypes.func.isRequired,
    onClearError: PropTypes.func.isRequired,
    modulesError: PropTypes.object,
    sessionBased: PropTypes.bool
};

export default ModuleTemplateManagement;
